import {
  Button,
  Checkbox,
  Col,
  Row,
  Select,
  Spin,
  Table,
  Tag,
  Typography,
  Card,
  message,
  Modal,
} from 'antd'
import { DeleteOutlined, EditOutlined, FacebookFilled } from '@ant-design/icons'
import { useEffect, useMemo, useState } from 'react'
import { fetchAdminUsers, editAdminUsers, fetchAdminInfo } from 'src/api/authen'
import { users } from 'src/api/audit'
import { availablePermissions, permissionPageMap } from 'src/constants'
import './Admin.css'

const { Option } = Select
const { Title, Text } = Typography

const Admin = () => {
  const [loading, setLoading] = useState(true)
  const [userData, setUserData] = useState([])
  const [username, setUsername] = useState('')
  const [searchUser, setSearchUser] = useState(null)

  const [selectedUser, setSelectedUser] = useState(null)
  const [selectedPermissions, setSelectedPermissions] = useState<string | null>(
    null
  )
  const [accessToUserInfo, setAccessToUserInfo] = useState(false)
  const [usersLoaded, setUsersLoaded] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [usersMap, setUsersMap] = useState<UsersMap>({})

  const [transformedData, setTransformedData] = useState([])
  const [adminUserData, setAdminUserData] = useState({
    data: [],
    current_page: 0,
    number_of_elements: 0,
    total_pages: 0,
    total_items: 0,
  })

  const fetchData = async (page, size, username) => {
    setLoading(true)
    try {
      const response = await fetchAdminUsers(page - 1, size, username)
      setAdminUserData(response)
      setTotalRecords(response.total_items)
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  const addUser = (userId, username) => {
    setUsersMap((prevUsersMap) => ({
      ...prevUsersMap,
      [userId]: username,
    }))
  }

  const fetchUserData = async () => {
    try {
      const response = await users()

      const usersList = Object.entries(response).map(([user_id, username]) => ({
        label: username,
        value: username,
        userId: user_id,
      }))

      usersList.forEach((user) => addUser(user.value, user.label))
      setUserData(usersList)
      setUsersLoaded(true)
    } catch (error) {
      console.error('Error fetching users:', error)
    }
  }

  const handleTableChange = (pagination: any) => {
    setCurrentPage(pagination.current)
    setPageSize(pagination.pageSize)
  }

  const handleDeleteUserPermissions = async (user) => {
    Modal.confirm({
      className: 'delete-confirm-modal',
      title: 'Remove User Permissions',
      content:
        'Are you sure you want to continue? This will remove all assigned permissions for this user.',
      okText: 'Continue',
      cancelText: 'Cancel',
      okButtonProps: {
        style: {
          border: 'none',
          color: '#fff',
          background: 'linear-gradient(90deg, #4c1d95 0%, #db2777 100%)',
        },
      },
      onOk: async () => {
        try {
          setLoading(true)

          await editAdminUsers(user, [], false)

          message.success('Permissions removed successfully')

          fetchData(currentPage, pageSize, username)
        } catch (error) {
          console.error(error)
          message.error('Failed to remove permissions')
        } finally {
          setLoading(false)
        }
      },
    })
  }

  const handleCreateOrUpdate = async () => {
    if (!selectedUser) {
      message.error('Please select user')
      return
    }

    try {
      setLoading(true)
      await editAdminUsers(
        selectedUser,
        selectedPermissions ? [selectedPermissions] : [],
        accessToUserInfo
      )
      setSelectedUser(null)
      setSelectedPermissions(null)
      setAccessToUserInfo(false)
      fetchData(currentPage, pageSize, username)
    } catch (error) {
      console.error(error)
      message.error('Failed to update permissions')
    } finally {
      setLoading(false)
    }
  }

  const selectUserForEdit = async (username) => {
    if (username === undefined || username === null) {
      setSelectedUser(null)
      setSelectedPermissions(null)
      setAccessToUserInfo(false)
    } else {
      setSelectedUser(username)
      setLoading(true)
      try {
        const adminInfo = await fetchAdminInfo(username)
        const validPermissions = adminInfo.permissions.filter((permission) =>
          availablePermissions.some((p) => p.value === permission)
        )
        setSelectedPermissions(validPermissions?.[0] || null)
        setAccessToUserInfo(adminInfo?.accessToUserInfo)
      } finally {
        setLoading(false)
      }
    }
  }

  const editAdminUsersPermission = async (record) => {
    setSelectedUser(record.user)
    const validPermissions = record.permissions.filter((permission) =>
      availablePermissions.some((p) => p.value === permission)
    )
    setSelectedPermissions(validPermissions?.[0] || null)
    const adminInfo = await fetchAdminInfo(record.user)
    setAccessToUserInfo(adminInfo?.accessToUserInfo)
  }

  const getPermissionLabel = (permission) => {
    return availablePermissions.find((p) => p.value === permission)?.label
  }

  const pages = useMemo(() => {
    if (!selectedPermissions) {
      return []
    }

    return permissionPageMap[selectedPermissions] || []
  }, [selectedPermissions])

  const columns = [
    {
      title: 'Username',
      dataIndex: 'user',
      key: 'user',
      render: (text) => (
        <Text style={{ color: '#fff', fontWeight: 500 }}>{text}</Text>
      ),
    },
    {
      title: 'Role Name',
      dataIndex: 'permissions',
      key: 'permissions',
      render: (permissions) => {
        const validPermissions = permissions.filter((permission) =>
          availablePermissions.some((p) => p.value === permission)
        )

        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {validPermissions.map((permission) => (
              <Tag
                key={permission}
                style={{
                  borderRadius: 20,
                  padding: '6px 14px',
                  background: '#5b21b6',
                  border: 'none',
                  color: '#fff',
                }}
              >
                {getPermissionLabel(permission)}
              </Tag>
            ))}
          </div>
        )
      },
    },
    {
      title: 'Pages',
      dataIndex: 'pages',
      key: 'pages',
      render: (_, record) => {
        const uniquePages = new Set<string>()

        record.permissions.forEach((permission) => {
          const pages = permissionPageMap[permission] || []

          pages.forEach((page) => uniquePages.add(page))
        })

        return (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
            }}
          >
            {Array.from(uniquePages).map((page) => (
              <Tag
                key={page}
                style={{
                  borderRadius: 20,
                  padding: '6px 14px',
                  background: '#db2777',
                  border: 'none',
                  color: '#fff',
                }}
              >
                {page}
              </Tag>
            ))}
          </div>
        )
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 120,
      render: (_, record) => {
        const isSuperAdmin = record.permissions.includes('superadmin')

        const hasPermissions =
          record.permissions && record.permissions.length > 0

        if (isSuperAdmin) {
          return null
        }

        return (
          <div style={{ display: 'flex', gap: 16 }}>
            <EditOutlined
              style={{
                color: '#5b21b6',
                fontSize: 18,
                cursor: 'pointer',
              }}
              onClick={() => {
                editAdminUsersPermission(record)
              }}
            />

            {hasPermissions && (
              <DeleteOutlined
                style={{
                  color: '#db2777',
                  fontSize: 18,
                  cursor: 'pointer',
                }}
                onClick={() => handleDeleteUserPermissions(record.user)}
              />
            )}
          </div>
        )
      },
    },
  ]

  useEffect(() => {
    const data =
      adminUserData.data && adminUserData.data.length
        ? Object.entries(adminUserData.data[0]).map(([key, value], index) => ({
            key: index,
            user: key,
            permissions: Array.isArray(value) ? value : [],
          }))
        : []

    setTransformedData(data)
  }, [adminUserData])

  useEffect(() => {
    fetchData(currentPage, pageSize, username)
  }, [pageSize, currentPage, username])

  useEffect(() => {
    fetchUserData()
  }, [])

  return (
    <div
      style={{
        background: '#000',
        minHeight: '100vh',
      }}
    >
      <Title
        level={4}
        style={{ color: '#fff', marginBottom: 10, marginLeft: 20 }}
      >
        User Roles
      </Title>

      <Spin spinning={loading}>
        <Row gutter={24}>
          <Col span={10}>
            <Card
              bordered={false}
              style={{
                background: '#050505',
                border: '1px solid #1f1f1f',
                borderRadius: 12,
                minHeight: 760,
              }}
              bodyStyle={{
                padding: 24,
              }}
            >
              <Title
                level={5}
                style={{
                  color: '#fff',
                  marginBottom: 24,
                }}
              >
                Assign Roles/Pages
              </Title>

              <Select
                placeholder="Username"
                allowClear
                options={userData}
                onChange={(value) => selectUserForEdit(value)}
                value={selectedUser}
                className="search-input"
                showSearch
                optionFilterProp="label"
                style={{ width: '100%' }}
              />

              <div style={{ marginBottom: 16 }}>
                <Select
                  value={selectedPermissions}
                  placeholder="Select Role"
                  style={{ width: '100%' }}
                  size="middle"
                  onChange={(value) => setSelectedPermissions(value)}
                >
                  {availablePermissions
                    .filter((permission) => permission.value !== 'superadmin')
                    .map((permission) => (
                      <Option key={permission.value} value={permission.value}>
                        {permission.label}
                      </Option>
                    ))}
                </Select>
              </div>

              <div
                style={{
                  background: '#0a0a0a',
                  border: '1px solid #1f1f1f',
                  borderRadius: 10,
                  minHeight: 320,
                  padding: 16,
                  marginBottom: 20,
                }}
              >
                {pages.length === 0 ? (
                  <Text style={{ color: '#666' }}>
                    All assign page names will show here
                  </Text>
                ) : (
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 10,
                    }}
                  >
                    {pages.map((page) => (
                      <Tag
                        key={page}
                        style={{
                          borderRadius: 20,
                          padding: '6px 14px',
                          background:
                            'linear-gradient(90deg, #5b21b6 0%, #db2777 100%)',
                          border: 'none',
                          color: '#fff',
                        }}
                      >
                        {page}
                      </Tag>
                    ))}
                  </div>
                )}
              </div>

              <Checkbox
                checked={accessToUserInfo}
                onChange={(e) => setAccessToUserInfo(e.target.checked)}
                style={{
                  color: '#d9d9d9',
                  marginBottom: 24,
                }}
              >
                Access to user information and export option
              </Checkbox>

              <Button
                type="primary"
                block
                size="large"
                onClick={handleCreateOrUpdate}
                style={{
                  height: 48,
                  border: 'none',
                  borderRadius: 8,
                  background:
                    'linear-gradient(90deg, #4c1d95 0%, #db2777 100%)',
                  fontWeight: 600,
                }}
              >
                Create
              </Button>
            </Card>
          </Col>

          <Col span={14}>
            <Card
              bordered={false}
              style={{
                background: '#050505',
                border: '1px solid #1f1f1f',
                borderRadius: 12,
                minHeight: 760,
              }}
              bodyStyle={{
                padding: 24,
              }}
            >
              <Title
                level={5}
                style={{
                  color: '#fff',
                  marginBottom: 24,
                }}
              >
                All Users
              </Title>
              <Select
                placeholder="Username"
                allowClear
                options={userData}
                onChange={(_, option) => {
                  setUsername(option?.label || '')
                  setSearchUser(option?.value || null)
                }}
                value={searchUser}
                className="search-input"
                showSearch
                optionFilterProp="label"
                style={{ width: '40%' }}
              />
              <Table
                dataSource={transformedData}
                columns={columns}
                rowKey="user"
                pagination={{
                  current: currentPage,
                  pageSize,
                  total: totalRecords,
                  showSizeChanger: true,
                }}
                onChange={handleTableChange}
                style={{
                  background: 'transparent',
                }}
                rowClassName={() => 'admin-dark-row'}
              />
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  )
}

export default Admin
